/**
 * Serviço para criação automática de fornecedores
 * Garante que fornecedores informados em transações sejam criados na tabela suppliers
 */

import { supabase } from '@/integrations/supabase/client';
import { v4 as uuidv4 } from 'uuid';

export interface SupplierAutoCreateResult {
  success: boolean;
  supplierId?: string;
  error?: string;
}

/**
 * Cria automaticamente um fornecedor na tabela suppliers se não existir
 */
export async function createSupplierIfNotExists(
  supplierName: string,
  userId: string
): Promise<SupplierAutoCreateResult> {
  if (!supplierName || !userId) {
    return { success: false, error: 'Nome do fornecedor e ID do usuário são obrigatórios' };
  }

  try {
    // Verificar se fornecedor já existe
    const { data: existingSupplier, error: checkError } = await supabase
      .from('suppliers')
      .select('id, name')
      .eq('user_id', userId)
      .ilike('name', supplierName.trim())
      .limit(1);

    if (checkError) {
      console.error('Erro ao verificar fornecedor existente:', checkError);
      return { success: false, error: checkError.message };
    }

    // Se fornecedor já existe, retornar ID existente
    if (existingSupplier && existingSupplier.length > 0) {
      return { 
        success: true, 
        supplierId: existingSupplier[0].id 
      };
    }

    // Criar novo fornecedor
    const newSupplierId = uuidv4();
    const { data: newSupplier, error: createError } = await supabase
      .from('suppliers')
      .insert({
        id: newSupplierId,
        user_id: userId,
        name: supplierName.trim(),
        document_type: 'cnpj', // Default para fornecedores
        document_number: `AUTO-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, // Valor único para auto-criados
        bank_account_type: 'corrente', // Valor padrão para satisfazer a constraint
        is_active: true
      })
      .select('id')
      .single();

    if (createError) {
      // Se já existe por constraint de documento, tentar reutilizar registro existente
      if (createError.code === '23505') {
        const { data: byName, error: refetchError } = await supabase
          .from('suppliers')
          .select('id')
          .eq('user_id', userId)
          .ilike('name', supplierName.trim())
          .limit(1);

        if (!refetchError && byName && byName.length > 0) {
          return { success: true, supplierId: byName[0].id };
        }
      }
      console.error('Erro ao criar fornecedor:', createError);
      return { success: false, error: createError.message };
    }

    console.log(`✅ Fornecedor "${supplierName}" criado automaticamente com ID: ${newSupplierId}`);
    
    return { 
      success: true, 
      supplierId: newSupplier.id 
    };

  } catch (error) {
    console.error('Erro inesperado ao criar fornecedor:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Erro desconhecido' 
    };
  }
}

/**
 * Sincroniza um fornecedor existente na transação com a tabela suppliers
 * Garante que o supplier_id seja preenchido corretamente
 */
export async function syncSupplierWithTransaction(
  transactionId: string,
  supplierName: string,
  userId: string
): Promise<SupplierAutoCreateResult> {
  if (!transactionId || !supplierName || !userId) {
    return { success: false, error: 'ID da transação, nome do fornecedor e ID do usuário são obrigatórios' };
  }

  try {
    // Primeiro, garantir que o fornecedor exista na tabela suppliers
    const supplierResult = await createSupplierIfNotExists(supplierName, userId);
    
    if (!supplierResult.success || !supplierResult.supplierId) {
      return supplierResult;
    }

    // Atualizar a transação com o supplier_id
    const { error: updateError } = await supabase
      .from('transactions')
      .update({ 
        supplier_id: supplierResult.supplierId,
        // Manter o campo supplier para compatibilidade
        supplier: supplierName 
      })
      .eq('id', transactionId);

    if (updateError) {
      console.error('Erro ao vincular fornecedor à transação:', updateError);
      return { success: false, error: updateError.message };
    }

    console.log(`✅ Fornecedor "${supplierName}" vinculado à transação ${transactionId}`);
    
    return { 
      success: true, 
      supplierId: supplierResult.supplierId 
    };

  } catch (error) {
    console.error('Erro inesperado ao sincronizar fornecedor:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Erro desconhecido' 
    };
  }
}

/**
 * Processa múltiplas transações para garantir fornecedores sincronizados
 */
export async function batchSyncSuppliers(
  transactions: Array<{ id: string; supplier?: string; user_id: string }>
): Promise<{ success: number; errors: Array<{ transactionId: string; error: string }> }> {
  const results = {
    success: 0,
    errors: [] as Array<{ transactionId: string; error: string }>
  };

  for (const transaction of transactions) {
    if (transaction.supplier && transaction.user_id) {
      const result = await syncSupplierWithTransaction(
        transaction.id,
        transaction.supplier,
        transaction.user_id
      );

      if (result.success) {
        results.success++;
      } else {
        results.errors.push({
          transactionId: transaction.id,
          error: result.error || 'Erro desconhecido'
        });
      }
    }
  }

  return results;
}
